import { useCallback, useEffect, useState } from 'react'
import { getConsolePathname, toConsolePath } from '../../utils/consoleRoutes'

// The app uses raw window.history.pushState in ~12 places (no router); the
// browser does NOT fire popstate for programmatic navigation, so a vanilla
// popstate-only listener misses every in-app route change. We wrap pushState
// and replaceState ONCE per page lifecycle to fire a custom event the hook can
// subscribe to. The wrap is keyed on a symbol so duplicate imports (HMR,
// multiple TourGuideProvider mounts) don't stack wrappers.
const NAVIGATION_EVENT = 'tour:locationchange'
type PatchedHistory = History & { __tourPatched?: boolean }

const installHistoryPatch = () => {
  if (typeof window === 'undefined') return
  const history = window.history as PatchedHistory
  if (history.__tourPatched) return
  history.__tourPatched = true
  const originalPush = history.pushState.bind(history)
  const originalReplace = history.replaceState.bind(history)
  history.pushState = (...args: Parameters<History['pushState']>) => {
    originalPush(...args)
    window.dispatchEvent(new Event(NAVIGATION_EVENT))
  }
  history.replaceState = (...args: Parameters<History['replaceState']>) => {
    originalReplace(...args)
    window.dispatchEvent(new Event(NAVIGATION_EVENT))
  }
}

if (typeof window !== 'undefined') installHistoryPatch()

export const useCurrentPath = () => {
  const [currentPath, setCurrentPath] = useState(() => getConsolePathname())

  useEffect(() => {
    const syncPath = () => setCurrentPath(getConsolePathname())
    window.addEventListener('popstate', syncPath)
    window.addEventListener(NAVIGATION_EVENT, syncPath)
    return () => {
      window.removeEventListener('popstate', syncPath)
      window.removeEventListener(NAVIGATION_EVENT, syncPath)
    }
  }, [])

  const navigate = useCallback((path: string) => {
    const nextPath = toConsolePath(path)
    if (window.location.pathname === nextPath) return
    window.history.pushState(null, '', nextPath)
    // pushState wrapper fires NAVIGATION_EVENT; no manual dispatch needed.
  }, [])

  return { currentPath, navigate }
}
